import { useFpB2bShippingService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-shipping-service'
import { CountryCode } from '@constants'
import { type CheckoutOrder } from '@hooks-api/types/dataModel/orders'
import { type DeliveryClass } from '@hooks-api/types/dataModel/shipping'
import { Alert } from '@pets-ui/Alert/Alert'
import { Box } from '@pets-ui/Box/Box'
import { Button } from '@pets-ui/Button/Button'
import { HStack } from '@pets-ui/HStack/HStack'
import { ArrowRightSmall } from '@pets-ui/icons'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import { fieldRefHelper } from '@utils/fieldRefHelper'
import { formatPrice } from '@utils/formatCurrency'
import { ShippingListItemRule } from '@wp-site-wide/components/ShippingListItemRule/ShippingListItemRule'
import { type ReactNode, useMemo, useState } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useParams } from 'react-router-dom-v5'
import { CardSection } from '../../../../../components/CardSection/CardSection'
import { useCheckout } from '../../../../fp-b2b-shared/hooks/use-checkout'
import { formatShippingPromotionDescription } from '../../../../fp-b2b-shared/utils/format-shipping-promote-description'
import { DialogShippingMethod } from './DialogShippingMethod'
import { ShippingMethodErrorMessage } from './ShippingMethodErrorMessage'
import { ShippingPromoteDescription } from './ShippingPromoteDescription'

export function ShippingMethod({ deliveryClass }: { deliveryClass: DeliveryClass }) {
  const [open, setOpen] = useState(false)
  const { trigger, formState: { errors }, control, clearErrors } = useFormContext<CheckoutOrder>()
  const { currency, campaignCode, campaignId, platform } = useCheckout()
  const { transactionId } = useParams<{ transactionId?: string }>()
  const { appliedShippingMethod, getAppliedShippingInfo } = useFpB2bShippingService(campaignId, campaignCode, platform, transactionId)

  const rules = useMemo(() => {
    return appliedShippingMethod?.shipping_rules?.[deliveryClass]
  }, [appliedShippingMethod, deliveryClass])

  const { minShippingAmount, minShippingFee } = useMemo(() => {
    return getAppliedShippingInfo(deliveryClass)
  }, [deliveryClass, getAppliedShippingInfo])

  const promoteDescription = useMemo(() => {
    if (!minShippingAmount || !minShippingFee) return null
    return formatShippingPromotionDescription(minShippingAmount, minShippingFee, currency)
  }, [minShippingAmount, minShippingFee, currency])

  const billingAddressState = useWatch({ control, name: 'billing_address_state' })
  const billingAddressCity = useWatch({ control, name: 'billing_address_city' })
  const billingAddressAddress1 = useWatch({ control, name: 'billing_address_address1' })
  const billingCountry = useWatch({ control, name: 'billing_country' })

  const isCompleteAddressRequired = [CountryCode.TW, CountryCode.HK].includes(billingCountry)
  const isValidAddress = !isCompleteAddressRequired || (billingAddressCity && billingAddressState)
  const isError = !billingAddressAddress1 || !isValidAddress

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleSubmit = async () => {
    await trigger(['billing_address_state', 'billing_address_city', 'billing_address_postcode', 'billing_address_address1'])
    if (errors.billing_address_address1 || errors.billing_address_city || errors.billing_address_state || errors.billing_address_postcode) return
    clearErrors('shippingMethodValidation')
    handleClose()
  }
  const getShippingRuleDescription = (fee: number): ReactNode => {
    return (
      <>
        運費
        {' '}
        {formatPrice(currency, fee)}
        {promoteDescription ? `；${promoteDescription}` : promoteDescription}
      </>
    )
  }

  const hasAddress = billingAddressState || billingAddressCity || billingAddressAddress1

  return (
    <>
      <CardSection title="配送方式">
        <Controller
          control={control}
          name="shippingMethodValidation"
          rules={{ validate: () => !isError || '請填寫收件資料！' }}
          render={({ field: { ref }, fieldState: { error } }) => {
            return (
              <HStack gap={2} alignItems="center" rootRef={fieldRefHelper(ref)}>
                <VStack gap={2} p={4} alignItems="stretch" borderStyle="solid" borderColor={colors.grey300} borderWidth={1} borderRadius={8} width="100%">
                  {promoteDescription && campaignCode && (
                    <ShippingPromoteDescription
                      campaignId={campaignId}
                      campaignCode={campaignCode}
                      minShippingAmount={minShippingAmount}
                      minShippingFee={minShippingFee}
                      platform={platform}
                    />
                  )}
                  <HStack gap={2} alignItems="center" justifyContent="space-between" flexWrap="nowrap">
                    <VStack gap={2}>
                      <Text fontWeight={600}>{appliedShippingMethod?.name}</Text>
                      {
                        rules?.map(rule => (
                          <ShippingListItemRule
                            key={rule.shipping_rule_id}
                            description={getShippingRuleDescription(rule.applied_shipping_fee.fee)}
                            deliveryClass={rule.delivery_class}
                          />
                        ))
                      }
                    </VStack>
                    <HStack justifyContent="flex-end">
                      <Button
                        data-testid="button-checkout-edit-shipping-method"
                        variant="text"
                        onClick={handleOpen}
                        endIcon={<ArrowRightSmall fontSize={16} color={colors.blue600} />}
                      >
                        <Text color={colors.blue600} noWrap>編輯</Text>
                      </Button>
                    </HStack>
                  </HStack>
                  {hasAddress
                    ? (
                      <Box gap={2} mt={2} backgroundColor={colors.grey50} p={3} borderRadius={3} width="100%">
                        <HStack>
                          <Text variant="body" color={colors.grey800}>
                            {billingAddressState}
                            {' '}
                            {billingAddressCity}
                            {' '}
                            {billingAddressAddress1}
                            {' '}
                          </Text>
                        </HStack>
                        {error?.message && <Text color={colors.red600}>{error.message}</Text>}
                      </Box>
                    )
                    : <Box mt={2} width="100%"><Alert type="warning">請編輯配送方式，填寫收件資訊</Alert></Box>}
                </VStack>
              </HStack>
            )
          }}
        />
        <ShippingMethodErrorMessage />
      </CardSection>
      <DialogShippingMethod open={open} onClose={handleClose} onSubmit={handleSubmit} />
    </>
  )
}
